import React from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Box from '../Atoms/Box';
import Typography from '../Atoms/Typography';
import Button from '../Atoms/Button/Button';

interface AlgoliaTablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const AlgoliaTablePagination: React.FC<AlgoliaTablePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        padding: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        flexShrink: 0,
      }}
    >
      <Button
        variant="outlined"
        size="small"
        onClick={handlePreviousPage}
        disabled={currentPage === 0}
        sx={{ minWidth: '40px', p: '6px' }}
      >
        <NavigateBeforeIcon />
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentPage > 2 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onPageChange(0)}
            sx={{ minWidth: '32px', p: '4px', fontWeight: 600 }}
          >
            1
          </Button>
        )}
        {currentPage > 3 && <Typography variant="body2">...</Typography>}
        {Array.from({ length: totalPages }, (_, i) => i)
          .filter(
            (page) =>
              page === currentPage ||
              page === currentPage - 1 ||
              page === currentPage + 1 ||
              (currentPage === 0 && page === 2) ||
              (currentPage === totalPages - 1 && page === totalPages - 3)
          )
          .map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onPageChange(page)}
              sx={{
                minWidth: '32px',
                p: '4px',
                fontWeight: page === currentPage ? 700 : 600,
                backgroundColor: page === currentPage ? 'primary.main' : undefined,
                color: page === currentPage ? 'white' : undefined,
              }}
              disabled={page === currentPage}
            >
              {page + 1}
            </Button>
          ))}
        {currentPage < totalPages - 4 && <Typography variant="body2">...</Typography>}
        {currentPage < totalPages - 3 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onPageChange(totalPages - 1)}
            sx={{ minWidth: '32px', p: '4px', fontWeight: 600 }}
          >
            {totalPages}
          </Button>
        )}
      </Box>
      <Button
        variant="outlined"
        size="small"
        onClick={handleNextPage}
        disabled={currentPage === totalPages - 1}
        sx={{ minWidth: '40px', p: '6px' }}
      >
        <NavigateNextIcon />
      </Button>
    </Box>
  );
};

export default AlgoliaTablePagination;
